import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CallSwiperLogo } from "@/components/CallSwiperLogo";
import { ArrowRight, Loader2, Check, Sparkles, MessagesSquare } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name || u.email}`);
      navigate(u.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex flex-col justify-between px-5 pt-10 pb-8 sm:p-8 lg:p-14 safe-top-margin safe-bottom">
        <CallSwiperLogo size={26} />
        <div className="max-w-md w-full mx-auto lg:mx-0 animate-fade-in-up">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Sign in</p>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
            Route every voice call.<br />
            <span style={{ color: "#CD9A6B" }}>Capture every lead.</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
            Multi-tenant control room for your Call Swiper voice agents. Recordings, transcripts, lead capture and analytics — one clean surface.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="login-email-input" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" className="h-11" />
            </div>
            <Button type="submit" className="w-full h-11 group bg-[#CD9A6B] hover:bg-[#BE8857] text-[#1a140f] font-semibold" disabled={busy} data-testid="login-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Sign in <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" /></>)}
            </Button>
            <div className="text-center">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Forgot password?
              </Link>
            </div>
          </form>
        </div>
        <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Call Swiper Technologies</p>
      </div>

      {/* Right: dark hero panel */}
      <div className="hidden lg:flex relative overflow-hidden" style={{ backgroundColor: "#0B0F17" }}>
        <div className="relative z-10 px-14 py-16 flex flex-col justify-center w-full max-w-2xl text-white">
          {/* Badge card */}
          <div className="inline-flex items-center gap-2 self-start rounded-full border border-white/10 bg-white/[0.04] backdrop-blur px-3 py-1.5">
            <Sparkles className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/80 font-semibold">Top Rated AI Voice Agent Platform</span>
          </div>

          {/* Heading */}
          <h2 className="mt-10 text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05]">
            Welcome to Call Swiper.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/60 max-w-lg">
            Route every voice call and capture every lead automatically with AI-powered voice agents.
          </p>

          {/* Checkmark features */}
          <ul className="mt-10 space-y-4">
            {[
              "Never miss a lead with 24/7 automated AI call handling and qualified routing.",
              "Real-time call transcripts, instant lead categorization, and detailed analytics in one dashboard.",
              "Seamlessly integrate voice agents with your existing CRM and sales workflows.",
              "Lower support costs and maximize conversion rates with instant AI follow-ups.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-blue-500/15 border border-blue-500/40 flex items-center justify-center">
                  <Check className="w-3 h-3 text-blue-400" strokeWidth={3} />
                </span>
                <span className="text-[14px] leading-relaxed text-white/80">{item}</span>
              </li>
            ))}
          </ul>

          {/* Footer callout */}
          <div className="mt-14 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 flex items-start gap-3">
            <MessagesSquare className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
            <p className="text-xs text-white/60 leading-relaxed">
              Contact sales or your Account Manager to unlock advanced AI workflows.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
